import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { validatePassword, validatePasswordMatch } from '../../utils/validation';
import { CheckCircle } from 'lucide-react';
interface PasswordResetModalProps {
  isOpen: boolean;
  onSubmit: (password: string) => Promise<void>;
}
export function PasswordResetModal({
  isOpen,
  onSubmit
}: PasswordResetModalProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{
    password?: string;
    confirm?: string;
    form?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: typeof errors = {};
    const passwordError = validatePassword(password);
    if (passwordError) newErrors.password = passwordError;
    if (!validatePasswordMatch(password, confirmPassword)) {
      newErrors.confirm = 'Passwords do not match';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(password);
      setIsSuccess(true);
    } catch (err) {
      setErrors({
        form: 'Failed to update password. Please try again.'
      });
      setIsSubmitting(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-608">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="absolute inset-0 bg-rose-900/20 backdrop-blur-sm" data-id="element-609" />

      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-8 overflow-hidden" data-id="element-610">
        {isSuccess ? <div className="text-center py-8" data-id="element-611">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-612">
              <CheckCircle size={32} data-id="element-613" />
            </motion.div>
            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2" data-id="element-614">
              Password Updated!
            </h2>
            <p className="text-gray-600" data-id="element-615">
              Your password has been reset successfully. You will be redirected
              shortly...
            </p>
          </div> : <>
            <div className="text-center mb-8" data-id="element-616">
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2" data-id="element-617">
                Update Your Password
              </h2>
              <p className="text-gray-600" data-id="element-618">
                For your security, please update your default password to
                something unique.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-619">
              <Input label="New Password" type="password" value={password} onChange={e => {
            setPassword(e.target.value);
            if (errors.password) setErrors({
              ...errors,
              password: undefined
            });
          }} error={errors.password} required data-id="element-620" />

              <Input label="Confirm New Password" type="password" value={confirmPassword} onChange={e => {
            setConfirmPassword(e.target.value);
            if (errors.confirm) setErrors({
              ...errors,
              confirm: undefined
            });
          }} error={errors.confirm} required data-id="element-621" />

              {errors.form && <p className="text-sm text-red-500 text-center" data-id="element-622">
                  {errors.form}
                </p>}

              <Button type="submit" className="w-full" isLoading={isSubmitting} data-id="element-623">
                Update Password
              </Button>
            </form>
          </>}
      </motion.div>
    </div>;
}